import { Component, HostBinding, OnDestroy, OnInit, } from '@angular/core';

import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Store, createSelector } from '@ngrx/store';
import { DialogModule } from 'primeng/dialog';
import { filter } from 'rxjs';
import { SubSink } from 'subsink';

import { ColorSchemeService } from '../color-scheme.service';
import { FreyaCommonModule } from '../freya-common/freya-common.module';

import { OnlineStatusService } from '../online-status.service';

import { KarveTabviewComponent } from "../shared/karve-tabview/karve-tabview.component";
import { UnsavedChangesBannerComponent } from '../shared/unsaved-changes-banner/unsaved-changes-banner.component';

import { selectJobPageHasSaveableChanges } from '../shared/unsaved-changes-banner/unsaved-changes-banner.selectors';

import { JobActivityComponent } from './job-activity/job-activity.component';
import { JobHeaderComponent } from './job-header/job-header.component';
import { TimelineBookEventComponent } from './job-overview/overview-timeline/timeline-book-event/timeline-book-event.component';
import { TimelineCreateEventComponent } from './job-overview/overview-timeline/timeline-create-event/timeline-create-event.component';
import { JobToolActions } from './job-tool.actions';
import { jobToolFeature, selectJobEditDisabledWarning, selectJobId } from './job-tool.reducer';


declare let Tawk_API;

@Component({
  selector: 'app-job',
  standalone: true,
  imports: [
    JobActivityComponent,
    FreyaCommonModule,
    JobHeaderComponent,
    KarveTabviewComponent,
    TimelineBookEventComponent,
    TimelineCreateEventComponent,
    DialogModule,
    UnsavedChangesBannerComponent,
  ],
  templateUrl: './job.component.html',
  styleUrls: ['./job.component.scss', './colors.scss'],
})
export class JobComponent implements OnInit, OnDestroy {

  public tabsWithSlug = [
    {
      name: 'Overview',
      slug: 'overview'
    },
    {
      name: 'Inventory',
      slug: 'inventory'
    },
    {
      name: 'Workorders',
      slug: 'workorders'
    },
    {
      name: 'Billing',
      slug: 'billing'
    },
    {
      name: 'Documents',
      slug: 'documents'
    }
  ];

  viewModel$ = this.store.select(createSelector({
    isDialogVisible: jobToolFeature.selectCreateEventDialogVisible,
    editDisabledWarning: selectJobEditDisabledWarning,
    baseUrl: createSelector(selectJobId, (jobId) => `/jobs/${jobId}`),
  }));

  /**
   * True when the active child route is the navigator — drives the
   * `navigator-active` host class which hides job chrome via CSS.
   * Cannot use `| async` in host bindings (requires JIT), so we subscribe
   * imperatively and store the result in a plain boolean.
   */
  @HostBinding('class.navigator-active') isNavigatorRoute = false;

  public JobToolActions = JobToolActions;

  private subs = new SubSink();

  constructor(
    public store: Store,
    public colorScheme: ColorSchemeService,
    public onlineStatusService: OnlineStatusService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    const checkNavigatorRoute = () => {
      this.isNavigatorRoute = /\/navigator\//.test(this.router.url);
    };
    checkNavigatorRoute(); // run immediately — URL is already set when ngOnInit fires
    this.subs.sink = this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
    ).subscribe(() => checkNavigatorRoute());

    this.subs.sink = this.store.select(selectJobPageHasSaveableChanges)
      // eslint-disable-next-line @ngrx/no-store-subscription
      .subscribe((hasUnsavedChanges) => {

        if (!Tawk_API || typeof Tawk_API === 'undefined') { return; }

        const isMobileOrTablet = window.innerWidth <= 1024;

        if (hasUnsavedChanges && isMobileOrTablet && typeof Tawk_API.hideWidget === 'function') {
          Tawk_API.hideWidget();
        } else if (typeof Tawk_API.showWidget === 'function') {
          Tawk_API.showWidget();
        }

      });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
